import { Filter } from "../dataAccess/DataSource";
import { Validator } from "../Types/Type";
import { FieldState } from "final-form";

export const err = (key: string, msg: string, args?: {}) => {
  throw new ValidationError(key, msg, args);
};

export class ValidationError extends Error {
  public key: string;
  public args?: {};

  public constructor(key: string, msg: string, args?: {}) {
    super(msg);
    this.key = key;
    this.args = args;
  }
}

export const valid = (
  condition: (v: any, allValues: any, meta: FieldState<any>) => boolean,
  key: string,
  msg: string,
  args?: {}
) => async (v: any, allValues: any, meta: FieldState<any>) =>
  !condition(v, allValues, meta) ? err(key, msg, args) : undefined;

export const asyncValid = (
  condition: (
    v: any,
    allValues: any,
    meta: FieldState<any>
  ) => Promise<boolean>,
  key: string,
  msg: string,
  args?: {}
) => async (v: any, allValues: any, meta: FieldState<any>) =>
  !(await condition(v, allValues, meta)) ? err(key, msg, args) : undefined;

const checkArray = (v: any) => {
  return (Array.isArray(v) && v.length !== 0) || !Array.isArray(v);
};

export const Required: Validator = valid(
  v => v !== undefined && v !== "" && v !== null && checkArray(v),
  "lego:validators.required",
  "Field is required"
);
export const Less = (max: number): Validator =>
  valid(v => v < max, "lego:validators.less", "Must be less then {{max}}", {
    max
  });
export const LessEq = (max: number): Validator =>
  valid(
    v => v <= max,
    "lego:validators.lessEq",
    "Must be less then or equal {{max}}",
    { max }
  );
export const More = (min: number): Validator =>
  valid(v => v > min, "lego:validators.more", "Must be more then {{min}}", {
    min
  });
export const MoreEq = (min: number): Validator =>
  valid(
    v => v >= min,
    "lego::validators.moreEq",
    "Must be more then or equal {{min}}",
    { min }
  );
export const Email: Validator = valid(
  (text: string) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(text).toLowerCase());
  },
  "lego:validators.email",
  "Must be an email"
);
export const Positive: Validator = More(0);
export const Negative: Validator = Less(0);
export const RangeInc = (min: number, max: number): Validator =>
  valid(
    v => v >= min && v <= max,
    "lego:validators.rangeInc",
    "Must be in range <{{min}}, {{max}}>",
    { min, max }
  );
export const RangeExc = (min: number, max: number): Validator =>
  valid(
    v => v > min && v < max,
    "lego:validators.rangeExc",
    "Must be in range ({{min}}, {{max}})",
    { min, max }
  );
export const Length = (arg1: number, arg2?: number) => {
  const min = arg2 === undefined ? 0 : arg1;
  const max = arg2 === undefined ? arg1 : arg2;

  return valid(
    v => {
      v = v || "";
      return v.length >= min && v.length <= max;
    },
    "lego:validators.length",
    "Length must be in range <{{min}}, {{max}}>",
    { min, max }
  );
};

export const NumbersArray: Validator = valid(
  v => {
    let result = true;
    if (v) {
      for (let i = 0; i < v.length; i++) {
        if (typeof v[i] !== "number" && !v[i].match("^[0-9]*$")) {
          return false;
        }
      }
    }
    return result;
  },
  "lego:validators.numbersArray",
  "Wrong input! Required number list"
);

export const CreateFilters = (names: string[], source: any) => {
  const filters: Filter[] = [];
  for (const name of names) {
    filters.push({ field: name, value: source[name] });
  }
  return filters;
};

export const Pattern = (regex: string, key: string, msg: string): Validator => {
  const re = new RegExp(regex);
  return valid(
    v => {
      v = v || "";
      return re.test(v);
    },
    key,
    msg
  );
};
